<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
      <!-- 1. 候補地追加モード -->
      <div v-if="mode === 'add'" class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">候補地を追加</h2>
          <button type="button" class="modal-close-btn" aria-label="Close" @click="$emit('close')">x</button>
        </div>
        <form @submit.prevent="submitAdd" class="modal-form" enctype="multipart/form-data">
          <div class="modal-body">
            <div class="form-group">
              <label class="form-label" for="add-candidate-name">候補地名 *</label>
              <input v-model="addForm.candidateName" type="text" id="add-candidate-name" class="form-input" placeholder="例: バンコク" required />
            </div>
            <div class="form-group">
              <label class="form-label" for="add-candidate-memo">メモ・魅力</label>
              <textarea v-model="addForm.memoText" id="add-candidate-memo" class="form-input form-textarea" placeholder="行きたい理由や魅力を入力しましょう"></textarea>
            </div>
            <div class="form-group">
              <label class="form-label" for="add-candidate-url">参考URL</label>
              <input v-model="addForm.urlText" type="url" id="add-candidate-url" class="form-input" placeholder="https://example.com" />
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label" for="add-candidate-budget">想定予算（合計・1人あたり）</label>
                <input v-model.number="addForm.estimatedBudget" type="number" id="add-candidate-budget" class="form-input" min="0" placeholder="内訳合計から自動計算されます" />
              </div>
              <div class="form-group">
                <label class="form-label" for="add-candidate-days">想定日数 (日)</label>
                <input v-model.number="addForm.estimatedDays" type="number" id="add-candidate-days" class="form-input" min="1" placeholder="例: 5" />
              </div>
            </div>
            <!-- 新規追加用の予算内訳 -->
            <div class="form-group" style="margin-top: 0.5rem;">
              <label class="form-label">予算内訳（1人あたり）</label>
              <div class="budget-breakdown-inputs" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; background: var(--bg-secondary); padding: 1rem; border-radius: 12px; border: 1px solid var(--border-color);">
                <div>
                  <label class="form-label-sub" for="add-budget-transit" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">交通費</label>
                  <input v-model.number="addForm.budgetBreakdown.transit" type="number" id="add-budget-transit" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="add-budget-accommodation" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">宿泊費</label>
                  <input v-model.number="addForm.budgetBreakdown.accommodation" type="number" id="add-budget-accommodation" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="add-budget-food" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">食費</label>
                  <input v-model.number="addForm.budgetBreakdown.food" type="number" id="add-budget-food" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="add-budget-other" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">その他</label>
                  <input v-model.number="addForm.budgetBreakdown.other" type="number" id="add-budget-other" class="form-input" min="0" />
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label" for="add-candidate-image">イメージ画像</label>
              <input type="file" id="add-candidate-image" @change="handleFileChange" accept="image/*" class="form-input-file" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="$emit('close')">キャンセル</button>
            <button type="submit" class="btn btn-primary" :disabled="isLoading">追加する</button>
          </div>
        </form>
      </div>

      <!-- 2. 候補地詳細モード -->
      <div v-else-if="mode === 'detail' && candidate" class="modal-container candidate-detail-modal">
        <div class="modal-header">
          <h2 class="modal-title">{{ candidate?.CANDIDATE_NAME }}</h2>
          <div class="header-actions" style="display: flex; gap: 0.75rem; align-items: center;">
            <button
              v-if="!isEditing"
              type="button"
              class="btn-edit-toggle"
              @click="startEdit"
              style="padding: 0.4rem 0.8rem; font-size: 0.8125rem; font-weight: 700; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; background: white;"
            >
              編集する
            </button>
            <button type="button" class="modal-close-btn" aria-label="Close" @click="$emit('close')">x</button>
          </div>
        </div>

        <div v-if="!isEditing" style="display: contents;">
          <div class="modal-body candidate-detail-body">
            <div class="detail-grid">
              <!-- 画像エリア -->
              <div class="detail-image-box no-image">
                <img
                  v-if="true"
                  :src="displayImageUrl"
                  :alt="'候補地: ' + candidate?.CANDIDATE_NAME"
                  loading="lazy"
                  @error="handleImageError"
                />
                <div v-else class="image-placeholder-large">
                  <span>{{ candidate?.CANDIDATE_NAME?.charAt(0) }}</span>
                </div>
              </div>

              <!-- スペックとスコア投票 -->
              <div class="detail-info-box">
                <div class="info-meta">
                  <div class="info-row">
                    <span class="label">ステータス</span>
                    <span class="status-badge" :class="statusClass">{{ candidate?.STATUS }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">想定予算</span>
                    <span class="val">{{ formatCurrency(candidate?.ESTIMATED_BUDGET) }}</span>
                  </div>
                  <div class="info-row" v-if="showDetailFields">
                    <span class="label">想定日数</span>
                    <span class="val">{{ candidate?.ESTIMATED_DAYS ? candidate?.ESTIMATED_DAYS + '日' : '未定' }}</span>
                  </div>
                </div>
                <button type="button" class="detail-toggle-button" @click="showDetailFields = !showDetailFields">
                  {{ showDetailFields ? "詳細を閉じる" : "詳細を見る" }}
                </button>

                <!-- 評価投票セクション -->
                <div class="score-voting-section">
                  <div class="score-summary-header">
                    <h4 class="score-title">
                      <svg class="star-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                      評価
                    </h4>
                    <span class="avg-score-badge" v-if="averageScoreLabel(candidate)">
                      平均評価: <strong class="avg-score-val">{{ averageScoreLabel(candidate) }}</strong>
                    </span>
                  </div>
                  <p class="section-desc">観光、アクティビティ、グルメ、費用を各5段階で評価してください。</p>
                  <div class="member-score-list category-score-list">
                    <div
                      v-for="member in ratingMembers"
                      :key="memberCode(member)"
                      class="member-score-row category-score-row"
                    >
                      <div class="member-score-heading">
                        <span class="member-score-name" :title="memberName(member)">{{ memberName(member) }}</span>
                        <strong>{{ memberScoreLabel(member) }}</strong>
                      </div>
                      <div class="category-score-grid">
                        <div v-for="category in scoreCategories" :key="`${memberCode(member)}-${category.key}`" class="category-score-control">
                          <span>{{ category.label }}</span>
                          <span class="score-stepper" role="group" :aria-label="`${memberName(member)} ${category.label}`">
                            <button
                              v-for="score in 5"
                              :key="`${memberCode(member)}-${category.key}-${score}`"
                              type="button"
                              class="score-number-btn"
                              :class="{ active: memberCategoryScore(member, category.key) === score }"
                              :aria-pressed="memberCategoryScore(member, category.key) === score ? 'true' : 'false'"
                              @click="submitMemberCategoryScore(member, category.key, score)"
                              :disabled="!canEditMemberScore(member)"
                            >
                              {{ score }}
                            </button>
                          </span>
                        </div>
                      </div>
                      <small v-if="canEditMemberScore(member)" class="score-save-hint">
                        {{ memberScoreSaveHint(member) }}
                      </small>
                      <button
                        v-if="memberScoreTotal(member) > 0 && canEditMemberScore(member)"
                        type="button"
                        class="member-clear-score category-clear-score"
                        :aria-label="`${memberName(member)}の評価をクリア`"
                        @click="$emit('clear-score', memberCode(member))"
                      >
                        クリア
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

<!-- 予算内訳の表示 -->
            <div class="detail-section" v-if="candidate?.BUDGET_BREAKDOWN && parseBudgetBreakdown(candidate?.BUDGET_BREAKDOWN)">
              <h4>想定予算の内訳</h4>
              <div class="budget-breakdown-display" style="display: flex; flex-direction: column; gap: 0.75rem;">
                <div v-for="(val, key) in parseBudgetBreakdown(candidate?.BUDGET_BREAKDOWN)" :key="key" class="budget-bar-row" style="display: flex; align-items: center; justify-content: space-between; font-size: 0.875rem;">
                  <span class="budget-bar-label" style="width: 80px; font-weight: 600; color: var(--text-muted);">{{ getBudgetKeyLabel(key) }}</span>
                  <div class="budget-bar-container" style="flex: 1; margin: 0 1rem; background: #e2e8f0; height: 8px; border-radius: 4px; overflow: hidden;">
                    <div class="budget-bar" :style="{ width: getBudgetBarWidth(val, candidate?.ESTIMATED_BUDGET), background: 'var(--primary-color)' }" style="height: 100%; border-radius: 4px;"></div>
                  </div>
                  <span class="budget-bar-value" style="width: 80px; text-align: right; font-weight: 700;">{{ formatCurrency(val) }}</span>
                </div>
              </div>
            </div>


            <!-- メモ -->
            <div class="detail-section" v-if="candidate?.MEMO_TEXT">
              <h4>メモ・魅力</h4>
              <p class="memo-content">{{ candidate?.MEMO_TEXT }}</p>
            </div>

            <!-- 参考URL -->
            <div class="detail-section" v-if="candidate?.URL_TEXT">
              <h4>参考リンク</h4>
              <a v-if="isSafeUrl(candidate?.URL_TEXT)" :href="candidate?.URL_TEXT" target="_blank" rel="noopener noreferrer" class="reference-link">
                {{ candidate?.URL_TEXT }}
              </a>
              <span v-else class="reference-link-invalid">
                {{ candidate?.URL_TEXT }}（セキュリティ保護のためリンク無効）
              </span>
            </div>

            <div class="detail-section">
              <h4>外部検索</h4>
              <div class="external-search-links">
              <a v-for="link in externalSearchLinks" :key="link.key" :href="link.url" :aria-label="link.label" :title="link.label" target="_blank" rel="noopener noreferrer" class="reference-link external-search-link" @click.stop>
                  <IconSvg :name="link.icon" custom-size="24px" decorative />
                </a>
              </div>
            </div>

            <!-- AI詳細調査レポート -->
            <div class="detail-section ai-report-section">
              <div class="ai-report-header">
                <h4>
                  <svg class="icon-inline robot-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                    <rect x="3" y="11" width="18" height="10" rx="2" fill="currentColor" fill-opacity="0.2" />
                    <path d="M12 2v4" />
                    <path d="M8 5h8" />
                    <circle cx="8" cy="16" r="1" fill="currentColor" />
                    <circle cx="16" cy="16" r="1" fill="currentColor" />
                  </svg>
                  AI詳細調査レポート
                </h4>
                <button
                  type="button"
                  class="btn-ai-research"
                  id="btn-run-ai-research"
                  data-track="board-ai-research"
                  :disabled="isAiResearching"
                  @click="$emit('run-ai', candidate?.CANDIDATE_NO)"
                >
                  <span v-if="isAiResearching" class="spinner-small"></span>
                  {{ candidate?.AI_RESEARCH_REPORT ? 'AIレポートを更新する' : 'AIで詳細を調査する' }}
                </button>
              </div>

              <div v-if="isAiResearching" class="ai-loading">
                <div class="spinner"></div>
                <p>AIが候補地の詳細（治安・ベストシーズン・観光スポット・おすすめの層）を調査しています...</p>
              </div>

              <div v-else-if="candidate?.AI_RESEARCH_REPORT" class="ai-report-content">
                <div
                  v-if="!isDomesticAiReport(parseAiReport(candidate?.AI_RESEARCH_REPORT))"
                  class="report-meta-grid"
                >
                  <div class="report-item">
                    <strong>
                      <svg class="icon-inline weather-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                        <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41m11.32-11.32l-1.41 1.41" />
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                      </svg>
                      ベストシーズン
                    </strong>
                    <p>{{ parseAiReport(candidate?.AI_RESEARCH_REPORT)?.bestSeason || parseAiReport(candidate?.AI_RESEARCH_REPORT)?.best_season || '-' }}</p>
                  </div>
                  <div class="report-item" v-if="showDetailFields">
                    <strong>
                      <svg class="icon-inline shield-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="currentColor" fill-opacity="0.2" />
                      </svg>
                      治安状況
                    </strong>
                    <p>{{ parseAiReport(candidate?.AI_RESEARCH_REPORT)?.safety || '-' }}</p>
                  </div>
                  <div class="report-item" v-if="parseAiReport(candidate?.AI_RESEARCH_REPORT)?.targetDemographics || parseAiReport(candidate?.AI_RESEARCH_REPORT)?.target_demographics">
                    <strong>
                      おすすめの層
                    </strong>
                    <p>{{ parseAiReport(candidate?.AI_RESEARCH_REPORT)?.targetDemographics || parseAiReport(candidate?.AI_RESEARCH_REPORT)?.target_demographics || '-' }}</p>
                  </div>
                  <div class="report-item">
                    <strong>
                      <svg class="icon-inline yen-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                        <path d="M6 3l6 8 6-8" />
                        <path d="M12 11v10" />
                        <path d="M8 15h8" />
                        <path d="M8 11h8" />
                      </svg>
                      予算目安
                    </strong>
                    <p>{{ parseAiReport(candidate?.AI_RESEARCH_REPORT)?.budgetRange || parseAiReport(candidate?.AI_RESEARCH_REPORT)?.budget_range || '想定予算通り' }}</p>
                  </div>
                </div>
                <div class="report-spots" v-if="showDetailFields">
                  <strong>
                    <svg class="icon-inline pin-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    おすすめ観光地・魅力
                  </strong>
                  
                  <!-- 配列の場合：カードグリッドレイアウト -->
                  <div v-if="isSpotsArray(parseAiReport(candidate?.AI_RESEARCH_REPORT))" class="spots-grid">
                    <div 
                      v-for="(spot, index) in getSpotsArray(parseAiReport(candidate?.AI_RESEARCH_REPORT))" 
                      :key="index" 
                      class="spot-card"
                    >
                      <div class="spot-card-image-box">
                        <img 
                          :src="spot.imageUrl || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=500&q=80'" 
                          :alt="spot.name + 'のイメージ'" 
                          loading="lazy" 
                        />
                      </div>
                      <div class="spot-card-info">
                        <h5 class="spot-card-title">{{ spot.name }}</h5>
                        <p class="spot-card-desc">{{ spot.description }}</p>
                        <a 
                          v-if="spot.referenceUrl" 
                          :href="spot.referenceUrl" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          class="spot-card-link-btn"
                        >
                          <svg class="icon-inline link-icon" viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="2" y1="12" x2="22" y2="12" />
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                          </svg>
                          詳しく見る
                        </a>
                      </div>
                    </div>
                  </div>

                  <!-- 文字列フォールバックの場合 -->
                  <p v-else class="report-spots-fallback-text">
                    {{ getSpotsText(parseAiReport(candidate?.AI_RESEARCH_REPORT)) }}
                  </p>
                </div>
              </div>

              <div v-else class="ai-report-empty">
                <p>AI調査レポートはまだ実行されていません。</p>
              </div>
            </div>
          </div>

          <div class="modal-footer candidate-detail-footer">
            <div class="footer-left">
              <button
                type="button"
                class="btn-danger-outline"
                @click="$emit('delete-candidate', candidate?.CANDIDATE_NO)"
              >
                候補から削除
              </button>
            </div>
            <div class="footer-right">
              <button
                type="button"
                class="btn btn-secondary btn-ai-consult"
                @click="$emit('consult-ai', candidate)"
              >
                旅程をAIに相談する
              </button>
              <button
                v-if="canFinalize"
                type="button"
                class="btn btn-primary btn-finalize"
                id="btn-finalize-shiori"
                data-track="board-finalize-shiori"
                :disabled="isLoading || isFinalizing"
                @click="$emit('finalize', candidate?.CANDIDATE_NO)"
              >
                <svg class="icon-inline party-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                  <path d="M22 2l-9 9m9-9l-3 6m3-6l-6 3M13 11l-3 4-2-2 4-3-3 1z" />
                  <path d="M11 4l1 1M16 11l1 1M7 9l1 1" />
                </svg>
                {{ isFinalizing ? "しおりを作成中..." : "この候補地に決定してしおりを作成する" }}
              </button>
            </div>
          </div>
        </div>

        <form v-else @submit.prevent="submitEdit" class="modal-form" enctype="multipart/form-data" style="display: contents;">
          <div class="modal-body candidate-detail-body">
            <div class="form-group">
              <label class="form-label" for="edit-candidate-image">イメージ画像を変更</label>
              <input type="file" id="edit-candidate-image" @change="handleEditFileChange" accept="image/*" class="form-input" style="padding: 0.5rem;" />
            </div>
            
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label" for="edit-candidate-days">想定日数 (日)</label>
                <input v-model.number="editForm.estimatedDays" type="number" id="edit-candidate-days" class="form-input" min="1" placeholder="例: 5" />
              </div>
              <div class="form-group">
                <label class="form-label" for="edit-candidate-budget">想定予算（合計・1人あたり）</label>
                <input v-model.number="editForm.estimatedBudget" type="number" id="edit-candidate-budget" class="form-input" min="0" placeholder="内訳合計から自動計算されます" />
              </div>
            </div>

            <!-- 内訳入力 -->
            <div class="form-group" style="margin-top: 0.5rem;">
              <label class="form-label">予算内訳（1人あたり）</label>
              <div class="budget-breakdown-inputs" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; background: var(--bg-secondary); padding: 1rem; border-radius: 12px; border: 1px solid var(--border-color);">
                <div>
                  <label class="form-label-sub" for="edit-budget-transit" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">交通費</label>
                  <input v-model.number="editForm.budgetBreakdown.transit" type="number" id="edit-budget-transit" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="edit-budget-accommodation" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">宿泊費</label>
                  <input v-model.number="editForm.budgetBreakdown.accommodation" type="number" id="edit-budget-accommodation" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="edit-budget-food" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">食費</label>
                  <input v-model.number="editForm.budgetBreakdown.food" type="number" id="edit-budget-food" class="form-input" min="0" />
                </div>
                <div>
                  <label class="form-label-sub" for="edit-budget-other" style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); display: block; margin-bottom: 0.25rem;">その他</label>
                  <input v-model.number="editForm.budgetBreakdown.other" type="number" id="edit-budget-other" class="form-input" min="0" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label" for="edit-candidate-memo">メモ・魅力</label>
              <textarea v-model="editForm.memoText" id="edit-candidate-memo" class="form-input form-textarea" placeholder="行きたい理由や魅力を入力しましょう"></textarea>
            </div>
</div>
          
          <div class="modal-footer">
            <div class="footer-left">
              <button type="button" class="btn btn-secondary" @click="isEditing = false">キャンセル</button>
            </div>
            <div class="footer-right">
              <button type="submit" class="btn btn-primary">保存する</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, computed } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { candidateExternalLinks, placeholderImage, resolveCandidateImage } from "@/utils/candidate-presentation.js";

export default {
  name: "CandidateModal",
  components: { IconSvg },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true, // 'add' | 'detail'
    },
    candidate: {
      type: Object,
      default: null,
    },
    myMemberCode: {
      type: String,
      default: null,
    },
    members: {
      type: Array,
      default: () => [],
    },
    candidateImageUrl: {
      type: String,
      default: "",
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
    isAiResearching: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isFinalizing: {
      type: Boolean,
      default: false,
    },
    canFinalize: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "close",
    "submit",
    "vote-score",
    "clear-score",
    "run-ai",
    "finalize",
    "delete-candidate",
    "update-candidate",
    "consult-ai",
  ],
  setup(props, { emit }) {
    const displayImageUrl = computed(() => resolveCandidateImage(props.candidate || {}, props.candidateImageUrl));
    const externalSearchLinks = computed(() => candidateExternalLinks(props.candidate?.CANDIDATE_NAME));
    const statusClass = computed(() => {
      if (props.candidate?.STATUS === "決定") return "is-finalized";
      if (props.candidate?.STATUS === "却下") return "is-rejected";
      return "is-considering";
    });
    // 旅程例のステップ化
    const itinerarySteps = computed(() => {
      if (!props.candidate?.ITINERARY_SAMPLE) return [];
      return props.candidate.ITINERARY_SAMPLE
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    });

    // おすすめ観光地ヘルパー
    const isSpotsArray = (report) => {
      if (!report) return false;
      const spots = report.spotRecommendations || report.foodRecommendations || report.recommendSpots || report.recommendedSpots || report.recommend_spots || report.spots;
      return Array.isArray(spots);
    };

    const getSpotsArray = (report) => {
      if (!report) return [];
      const modern = [
        ...(Array.isArray(report.spotRecommendations) ? report.spotRecommendations : []),
        ...(Array.isArray(report.foodRecommendations) ? report.foodRecommendations : []),
      ];
      if (modern.length) return modern;
      const spots = report.recommendSpots || report.recommendedSpots || report.recommend_spots || report.spots;
      return Array.isArray(spots)
        ? spots.map((spot) =>
            typeof spot === "string"
              ? { name: spot, description: "", imageUrl: "", referenceUrl: "" }
              : spot
          )
        : [];
    };

    const getSpotsText = (report) => {
      if (!report) return "-";
      const spots = report.recommendSpots || report.recommendedSpots || report.recommend_spots || report.spots;
      return typeof spots === "string" ? spots : "-";
    };

    // 1. 新規追加フォーム状態
    const addForm = ref({
      candidateName: "",
      memoText: "",
      urlText: "",
      estimatedBudget: null,
      estimatedDays: null,
      itinerarySample: "",
      budgetBreakdown: {
        transit: null,
        accommodation: null,
        food: null,
        other: null
      }
    });
    const candidateImageFile = ref(null);

    // 新規追加モード時の予算内訳自動合計同期
    watch(
      () => [
        addForm.value.budgetBreakdown.transit,
        addForm.value.budgetBreakdown.accommodation,
        addForm.value.budgetBreakdown.food,
        addForm.value.budgetBreakdown.other,
      ],
      ([transit, accommodation, food, other]) => {
        const total =
          Number(transit || 0) +
          Number(accommodation || 0) +
          Number(food || 0) +
          Number(other || 0);
        if (total > 0) {
          addForm.value.estimatedBudget = total;
        }
      }
    );

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) {
        candidateImageFile.value = null;
        return;
      }

      // MIMEタイプチェック
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        alert("JPEG, PNG, GIF, WebP の画像のみアップロードできます");
        e.target.value = "";
        candidateImageFile.value = null;
        return;
      }

      // サイズチェック（5MB）
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("画像サイズは5MB以下にしてください");
        e.target.value = "";
        candidateImageFile.value = null;
        return;
      }

      candidateImageFile.value = file;
    };

    const submitAdd = () => {
      const total =
        Number(addForm.value.budgetBreakdown.transit || 0) +
        Number(addForm.value.budgetBreakdown.accommodation || 0) +
        Number(addForm.value.budgetBreakdown.food || 0) +
        Number(addForm.value.budgetBreakdown.other || 0);
      
      if (total > 0) {
        addForm.value.estimatedBudget = total;
      }

      emit("submit", {
        form: { ...addForm.value },
        file: candidateImageFile.value,
      });
      // フォームクリア
      addForm.value = {
        candidateName: "",
        memoText: "",
        urlText: "",
        estimatedBudget: null,
        estimatedDays: null,
        itinerarySample: "",
        budgetBreakdown: {
          transit: null,
          accommodation: null,
          food: null,
          other: null
        }
      };
      candidateImageFile.value = null;
    };

    // 2. 詳細表示の星評価制御
    const localTempScore = ref(0);
    const showDetailFields = ref(false);
    const scoreDrafts = ref({});

    const ratingMembers = computed(() => {
      if (props.members && props.members.length > 0) return props.members;
      if (props.myMemberCode) return [{ MEMBER_CODE: props.myMemberCode, MEMBER_NAME: "評価" }];
      return [];
    });

    const scoreCategories = [
      { key: "sightseeing", payloadKey: "sightseeingScore", label: "観光" },
      { key: "activity", payloadKey: "activityScore", label: "アクティビティ" },
      { key: "food", payloadKey: "foodScore", label: "グルメ" },
      { key: "cost", payloadKey: "costScore", label: "費用" },
    ];

    const normalizeScoreDetails = (value) => {
      if (!value || typeof value !== "object") return null;
      const source = value.scores || value.scoreDetails || value.score_details || value;
      const details = {
        sightseeing: Number(source.sightseeingScore ?? source.sightseeing ?? source.tourism ?? source.SIGHTSEEING ?? 0) || 0,
        activity: Number(source.activityScore ?? source.activity ?? source.ACTIVITY ?? 0) || 0,
        food: Number(source.foodScore ?? source.gourmetScore ?? source.food ?? source.gourmet ?? source.FOOD ?? source.GOURMET ?? 0) || 0,
        cost: Number(source.costScore ?? source.cost ?? source.COST ?? 0) || 0,
      };
      return scoreCategories.some((category) => details[category.key] > 0) ? details : null;
    };

    const completeScoreDetails = (details) => {
      if (!details) return false;
      return scoreCategories.every((category) => {
        const value = Number(details[category.key] || 0);
        return value >= 1 && value <= 5;
      });
    };

    const scoreDetailsTotal = (details) =>
      scoreCategories.reduce((sum, category) => sum + Number(details?.[category.key] || 0), 0);

    const completePayloadScoreDetails = (details) =>
      scoreCategories.reduce((payload, category) => ({
        ...payload,
        [category.key]: Math.min(5, Math.max(1, Number(details?.[category.key] || 3) || 3)),
      }), {});

    const rawMemberScore = (code) => {
      if (!props.candidate || !code) return null;
      const detailScore = props.candidate.memberScoreDetails?.[code]
        ?? props.candidate.member_score_details?.[code]
        ?? props.candidate.scoreDetailsByMember?.[code]
        ?? props.candidate.score_details_by_member?.[code];
      if (normalizeScoreDetails(detailScore)) return detailScore;
      const scoreRow = props.candidate.scores?.find((s) => s.MEMBER_CODE === code || s.memberCode === code);
      if (normalizeScoreDetails(scoreRow)) return scoreRow;
      const directScore = props.candidate.memberScores?.[code];
      if (normalizeScoreDetails(directScore)) return directScore;
      if (detailScore !== undefined && detailScore !== null) return detailScore;
      if (scoreRow) return scoreRow;
      if (directScore !== undefined && directScore !== null) return directScore;
      return null;
    };


    const scoreInfo = (value) => {
      const details = normalizeScoreDetails(value);
      if (details) {
        return {
          value: scoreCategories.reduce((sum, category) => sum + Number(details[category.key] || 0), 0),
          max: 20,
          legacy: false,
          details,
        };
      }
      const raw = value && typeof value === "object"
        ? Number(value.SCORE_VAL ?? value.scoreVal ?? value.score ?? 0) || 0
        : Number(value || 0) || 0;
      if (raw > 5) return { value: raw, max: 20, legacy: false, details: null };
      return raw > 0 ? { value: raw, max: 5, legacy: true, details: null } : { value: 0, max: 0, legacy: false, details: null };
    };

    const memberScoreInfo = (member = {}) => scoreInfo(rawMemberScore(memberCode(member)));
    const scoreDraftKey = (member = {}) => String(memberCode(member) || "");
    const persistedMemberScoreDetails = (member = {}) => memberScoreInfo(member).details;
    const memberScoreDetails = (member = {}) => {
      const draft = scoreDrafts.value[scoreDraftKey(member)];
      return draft || persistedMemberScoreDetails(member);
    };

    const memberCategoryScore = (member = {}, key) => memberScoreDetails(member)?.[key] || 0;

    const memberScoreTotal = (member = {}) => {
      const draft = scoreDrafts.value[scoreDraftKey(member)];
      return draft ? scoreDetailsTotal(draft) : memberScoreInfo(member).value;
    };

    const memberScoreLabel = (member = {}) => {
      const draft = scoreDrafts.value[scoreDraftKey(member)];
      if (draft && !completeScoreDetails(draft)) {
        const selectedCount = scoreCategories.filter((category) => Number(draft[category.key] || 0) >= 1).length;
        return `${selectedCount}/4 selected`;
      }
      if (draft && completeScoreDetails(draft)) return `${scoreDetailsTotal(draft)}/20`;
      const info = memberScoreInfo(member);
      if (!info.value) return "Not rated";
      return info.legacy ? `旧評価 ${info.value}/5` : `${info.value}/20`;
    };

    const memberScoreSaveHint = (member = {}) => {
      const draft = scoreDrafts.value[scoreDraftKey(member)];
      if (!draft) return "Select all 4 items to save";
      if (completeScoreDetails(draft)) return "保存済み";
      const remaining = scoreCategories.length - scoreCategories.filter((category) => Number(draft[category.key] || 0) >= 1).length;
      return `${remaining} items left`;
    };
    const memberCode = (member = {}) => member.MEMBER_CODE || member.memberCode || "";
    const memberName = (member = {}) => member.MEMBER_NAME || member.memberName || "ゲスト";


    const canEditMemberScore = (member = {}) => {
      const code = memberCode(member);
      return Boolean(props.isOwner || (props.myMemberCode && code === props.myMemberCode));
    };
    const memberScore = memberScoreTotal;

    const getMyScore = (cand) => {
      if (!cand || !props.myMemberCode) return 0;
      return memberScore({ MEMBER_CODE: props.myMemberCode });
    };

    const getAverageScore = (cand) => {
      if (!cand) return 0;
      const infos = ratingMembers.value.map((member) => memberScoreInfo(member)).filter((info) => info.value > 0);
      if (infos.length) return infos.reduce((sum, info) => sum + info.value, 0) / infos.length;
      if (cand.averageScore !== undefined && cand.averageScore !== null) return Number(cand.averageScore);
      return 0;
    };

    const averageScoreLabel = () => {
      const infos = ratingMembers.value.map((member) => memberScoreInfo(member)).filter((info) => info.value > 0);
      if (!infos.length) return "";
      const avg = infos.reduce((sum, info) => sum + info.value, 0) / infos.length;
      const hasNew = infos.some((info) => !info.legacy);
      return hasNew ? `${avg.toFixed(1)}/20` : `旧評価 ${avg.toFixed(1)}/5`;
    };

    const handleMouseOver = (star) => {
      localTempScore.value = star;
    };

    const handleMouseLeave = () => {
      localTempScore.value = getMyScore(props.candidate);
    };

    const submitScore = (val) => {
      emit("vote-score", val);
      localTempScore.value = val;
    };

    const submitMemberScore = (member, score) => {
      const code = memberCode(member);
      if (!code) return;
      emit("vote-score", { memberCode: code, score });
    };
    const submitMemberCategoryScore = (member, key, score) => {
      const code = memberCode(member);
      if (!code || !canEditMemberScore(member)) return;
      const currentDraft = scoreDrafts.value[scoreDraftKey(member)];
      const baseScores = currentDraft || persistedMemberScoreDetails(member) || { sightseeing: 0, activity: 0, food: 0, cost: 0 };
      const scores = { ...baseScores, [key]: score };
      scoreDrafts.value = { ...scoreDrafts.value, [scoreDraftKey(member)]: scores };
      const payloadScores = completePayloadScoreDetails(scores);
      const total = scoreDetailsTotal(payloadScores);
      emit("vote-score", {
        memberCode: code,
        scoreVal: total,
        score: total,
        sightseeingScore: payloadScores.sightseeing,
        activityScore: payloadScores.activity,
        foodScore: payloadScores.food,
        costScore: payloadScores.cost,
      });
    };

    watch(() => props.candidate, (newCand) => {
      if (newCand) {
        localTempScore.value = getMyScore(newCand);
      } else {
        localTempScore.value = 0;
      }
      scoreDrafts.value = {};
    }, { immediate: true });

    // 3. ユーティリティ
    const formatCurrency = (val) => {
      if (val === null || val === undefined) return "未定";
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        maximumFractionDigits: 0,
      }).format(val);
    };

    const parseAiReport = (reportText) => {
      if (!reportText) return null;
      try {
        const parsed = JSON.parse(reportText);
        if (parsed && parsed.safety === "-" && parsed.report && parsed.report.includes("取得できませんでした")) {
          return {
            safety: parsed.report,
            bestSeason: "-",
            recommendSpots: "-",
            targetDemographics: "-"
          };
        }
        return parsed;
      } catch (e) {
        return { safety: reportText, bestSeason: "", recommendSpots: "" };
      }
    };

    const isDomesticAiReport = (report) => {
      if (!report) return false;
      const signal = [
        report.tripType,
        report.trip_type,
        report.destinationType,
        report.destination_type,
        report.country,
        report.region,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return signal.includes("domestic") || signal.includes("japan");
    };

    const isSafeUrl = (url) => {
      if (!url) return false;
      try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch (e) {
        return /^https?:\/\//i.test(url);
      }
    };

    // 4. インプレース基本情報編集
    const isEditing = ref(false);
    const editImageFile = ref(null);

    watch(
      () => props.show,
      (newVal) => {
        if (!newVal) {
          isEditing.value = false;
          editImageFile.value = null;
          scoreDrafts.value = {};
        }
      }
    );
    const editForm = ref({
      estimatedBudget: 0,
      estimatedDays: 0,
      memoText: "",
      itinerarySample: "",
      budgetBreakdown: {
        transit: 0,
        accommodation: 0,
        food: 0,
        other: 0
      }
    });

    // 編集モード時の予算内訳自動合計同期
    watch(
      () => [
        editForm.value.budgetBreakdown.transit,
        editForm.value.budgetBreakdown.accommodation,
        editForm.value.budgetBreakdown.food,
        editForm.value.budgetBreakdown.other,
      ],
      ([transit, accommodation, food, other]) => {
        const total =
          Number(transit || 0) +
          Number(accommodation || 0) +
          Number(food || 0) +
          Number(other || 0);
        if (total > 0) {
          editForm.value.estimatedBudget = total;
        }
      }
    );

    const parseBudgetBreakdown = (breakdownStr) => {
      if (!breakdownStr) return null;
      let rawObj = null;
      try {
        if (typeof breakdownStr === "object") {
          rawObj = breakdownStr;
        } else {
          rawObj = JSON.parse(breakdownStr);
        }
      } catch (e) {
        return null;
      }

      if (!rawObj) return null;

      // 日本語キーから英語キーへのフォールバック（正規化）
      const normalized = {
        transit: rawObj.transit !== undefined ? rawObj.transit : (rawObj["交通費"] !== undefined ? rawObj["交通費"] : 0),
        accommodation: rawObj.accommodation !== undefined ? rawObj.accommodation : (rawObj["宿泊費"] !== undefined ? rawObj["宿泊費"] : 0),
        food: rawObj.food !== undefined ? rawObj.food : (rawObj["食費"] !== undefined ? rawObj["食費"] : 0),
        other: rawObj.other !== undefined ? rawObj.other : (rawObj["その他"] !== undefined ? rawObj["その他"] : 0)
      };

      return normalized;
    };

    const getBudgetKeyLabel = (key) => {
      const labels = {
        transit: "交通費",
        accommodation: "宿泊費",
        food: "食費",
        other: "その他",
      };
      return labels[key] || key;
    };

    const getBudgetBarWidth = (val, total) => {
      if (!total) return "0%";
      const percentage = Math.min((Number(val || 0) / total) * 100, 100);
      return isNaN(percentage) ? "0%" : `${percentage}%`;
    };

    const handleEditFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) {
        editImageFile.value = null;
        return;
      }
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        alert("JPEG, PNG, GIF, WebP の画像のみアップロードできます");
        e.target.value = "";
        editImageFile.value = null;
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("画像サイズは5MB以下にしてください");
        e.target.value = "";
        editImageFile.value = null;
        return;
      }
      editImageFile.value = file;
    };

    const startEdit = () => {
      const breakdown = parseBudgetBreakdown(props.candidate?.BUDGET_BREAKDOWN) || {
        transit: 0,
        accommodation: 0,
        food: 0,
        other: 0
      };
      editForm.value = {
        estimatedBudget: props.candidate?.ESTIMATED_BUDGET || 0,
        estimatedDays: props.candidate?.ESTIMATED_DAYS || 0,
        memoText: props.candidate?.MEMO_TEXT || "",
        itinerarySample: props.candidate?.ITINERARY_SAMPLE || "",
        budgetBreakdown: {
          transit: breakdown.transit || 0,
          accommodation: breakdown.accommodation || 0,
          food: breakdown.food || 0,
          other: breakdown.other || 0
        }
      };
      editImageFile.value = null;
      isEditing.value = true;
    };

    const submitEdit = () => {
      const total =
        Number(editForm.value.budgetBreakdown.transit) +
        Number(editForm.value.budgetBreakdown.accommodation) +
        Number(editForm.value.budgetBreakdown.food) +
        Number(editForm.value.budgetBreakdown.other);
      
      if (total > 0) {
        editForm.value.estimatedBudget = total;
      }

      const formData = new FormData();
      formData.append("estimatedBudget", editForm.value.estimatedBudget || 0);
      formData.append("estimatedDays", editForm.value.estimatedDays || "");
      formData.append("memoText", editForm.value.memoText || "");
      formData.append("itinerarySample", editForm.value.itinerarySample || "");
      formData.append("budgetBreakdown", JSON.stringify(editForm.value.budgetBreakdown));

      if (editImageFile.value) {
        formData.append("image", editImageFile.value);
      }

      emit("update-candidate", {
        candidateNo: props.candidate.CANDIDATE_NO,
        formData: formData
      });
      isEditing.value = false;
    };

    watch(
      () => props.show,
      (newShow) => {
        if (!newShow) {
          isEditing.value = false;
          candidateImageFile.value = null;
          editImageFile.value = null;
          scoreDrafts.value = {};
        }
      }
    );

    const handleImageError = (e) => {
      e.target.src = placeholderImage;
    };

    return {
      displayImageUrl,
      externalSearchLinks,
      statusClass,
      handleImageError,
      addForm,
      candidateImageFile,
      localTempScore,
      showDetailFields,
      scoreCategories,
      memberCategoryScore,
      memberScoreTotal,
      memberScoreLabel,
      memberScoreSaveHint,
      averageScoreLabel,
      canEditMemberScore,
      handleFileChange,
      submitAdd,
      getMyScore,
      getAverageScore,
      ratingMembers,
      memberCode,
      memberName,
      memberScore,
      submitMemberScore,
      submitMemberCategoryScore,
      handleMouseOver,
      handleMouseLeave,
      submitScore,
      formatCurrency,
      parseAiReport,
      isDomesticAiReport,
      isSafeUrl,
      isEditing,
      editImageFile,
      editForm,
      parseBudgetBreakdown,
      getBudgetKeyLabel,
      getBudgetBarWidth,
      handleEditFileChange,
      startEdit,
      submitEdit,
      itinerarySteps,
      isSpotsArray,
      getSpotsArray,
      getSpotsText,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.68);
  /* 奥行きのある美しい Glassmorphism */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  background-image: radial-gradient(rgba(180, 207, 158, 0.06) 1px, transparent 0);
  background-size: 16px 16px;
}

.modal-container {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.candidate-detail-modal {
  max-width: 780px;
}

.modal-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--text-muted);
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  color: var(--text-main);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.modal-body {
  padding: 1.75rem;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  display: block;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: var(--primary-color);
}

.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: white;
  border: 1px solid #cbd5e1;
  color: var(--text-muted);
}

.btn-secondary:hover {
  background: var(--color-background);
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  border: none;
}

.btn-primary:hover {
  background: var(--primary-hover);
}

/* 候補地詳細 */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.50rem;
  margin-bottom: 1.50rem;
}

@media (min-width: 640px) {
  .detail-grid {
    grid-template-columns: 3fr 4fr;
  }
}

.detail-image-box {
  height: 220px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.detail-image-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder-large {
  height: 100%;
  background: linear-gradient(135deg, #93c5fd 0%, #cbd5e1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  color: white;
  font-weight: 800;
}

.detail-info-box {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.info-meta {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9375rem;
}

.info-row .label {
  color: var(--text-muted);
  font-weight: 600;
}

.info-row .val {
  font-weight: 700;
  color: var(--text-main);
}

.status-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
  color: white;
}

.status-badge.is-considering {
  background-color: var(--primary-color);
}

.status-badge.is-finalized {
  background-color: var(--primary-color);
}

.status-badge.is-rejected {
  background-color: #ef4444;
}

.score-voting-section {
  background-color: var(--color-background);
  padding: 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--color-border);
}

.score-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.score-title {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-main);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.score-title .star-icon {
  width: 16px;
  height: 16px;
  fill: var(--accent-gold);
}

.avg-score-badge {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.avg-score-val {
  color: var(--accent-gold);
}

.section-desc {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 0 0 0.75rem 0;
}

.member-score-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.member-score-row {
  display: grid;
  grid-template-columns: minmax(88px, 1fr) auto 24px;
  align-items: center;
  gap: 0.5rem;
}

.member-score-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 700;
}

.member-score-stars {
  gap: 0.25rem;
  font-size: 1.25rem;
}

.member-score-stars .voting-star {
  width: 22px;
  height: 22px;
}

.member-clear-slot {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.member-clear-score {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(239, 68, 68, 0.2);
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  font-size: 1rem;
  line-height: 1;
  font-weight: 800;
  cursor: pointer;
}

.member-clear-score:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
}

.voting-stars {
  font-size: 2.25rem;
  display: flex;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.voting-star {
  width: 32px;
  height: 32px;
  fill: #cbd5e1;
  transition: transform 0.2s ease, fill 0.2s ease, filter 0.2s ease;
}

.voting-star:hover {
  transform: scale(1.05);
}

.voting-star.filled {
  fill: var(--accent-gold);
  filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.4));
}

.btn-clear-score {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  margin-top: 0.75rem;
  transition: all 0.2s ease;
  display: inline-block;
}

.btn-clear-score:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  transform: translateY(-1px);
}

.score-finalize-btn {
  width: 100%;
  margin-top: 0.75rem;
}

.detail-section {
  margin-top: 1.5rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  padding-top: 1.25rem;
}

.detail-section h4 {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0 0 0.75rem 0;
  font-family: 'Space Grotesk', sans-serif;
}

.memo-content {
  font-size: 0.9375rem;
  color: var(--text-main);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.reference-link {
  font-size: 0.875rem;
  color: var(--primary-color);
  text-decoration: underline;
  word-break: break-all;
}


.detail-toggle-button {
  align-self: flex-start;
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: white;
  color: var(--primary-color);
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 800;
}

.category-score-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  padding: 0.75rem;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 12px;
}

.member-score-heading {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
}

.category-score-grid {
  display: grid;
  gap: 0.55rem;
}

.category-score-control {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  gap: 0.5rem;
  align-items: center;
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 700;
}

.score-stepper {
  display: flex;
  gap: 0.25rem;
}

.score-number-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: var(--text-main);
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 800;
}

.score-number-btn.active {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: white;
}

.score-save-hint {
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.category-clear-score {
  width: auto;
  min-width: 4.25rem;
  height: 28px;
  border-radius: 999px;
  font-size: 0.75rem;
}

.external-search-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.external-search-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: #ffffff;
  border: 1px solid rgba(30, 58, 95, 0.18);
  border-radius: 12px;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.external-search-link:hover {
  border-color: rgba(30, 58, 95, 0.48);
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.external-search-link:active { transform: translateY(0) scale(0.96); }

.external-search-link:focus-visible {
  outline: 3px solid #1e3a5f;
  outline-offset: 3px;
}

.btn-ai-consult {
  color: var(--primary-color);
  border-color: rgba(180, 207, 158, 0.35);
}
/* AIレポート */
.ai-report-section {
  background-color: #eff6ff;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(180, 207, 158, 0.16);
  margin-top: 1.5rem;
}

.ai-report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.btn-ai-research {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: background-color 0.2s;
}

.btn-ai-research:hover {
  background: var(--primary-hover);
}

.ai-loading {
  text-align: center;
  padding: 1.5rem 0;
}

.ai-report-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.report-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.report-item {
  background: white;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.report-item strong {
  font-size: 0.75rem;
  color: var(--text-muted);
  display: block;
  margin-bottom: 0.25rem;
}

.report-item p {
  font-size: 0.875rem;
  color: var(--text-main);
  margin: 0;
  font-weight: 700;
}

.report-spots {
  background: white;
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.report-spots p {
  font-size: 0.875rem;
  color: var(--text-main);
  line-height: 1.6;
  margin: 0.25rem 0 0 0;
}

.ai-report-empty {
  text-align: center;
  color: var(--text-muted);
  padding: 1rem 0;
}

.modal-footer {
  padding: 1.25rem 1.75rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--color-background);
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.modal-footer .footer-left,
.modal-footer .footer-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.modal-footer .footer-right {
  justify-content: flex-end;
  margin-left: auto;
}

.btn-danger-outline {
  background: none;
  border: 1px solid #fca5a5;
  color: #ef4444;
  padding: 0.625rem 1.25rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger-outline:hover {
  background: rgba(239, 68, 68, 0.05);
}

.btn-finalize {
  border-radius: 24px !important;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%) !important;
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.3);
  border: none;
  color: white;
}

.btn-finalize:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(180, 207, 158, 0.4);
}

/* アニメーション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.btn-star-vote {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: none;
  display: inline-flex;
}

.btn-star-vote:focus {
  outline: 2px solid var(--primary-color);
  border-radius: 4px;
}

.btn-star-vote:hover .voting-star {
  transform: scale(1.05);
}

.reference-link-invalid {
  color: var(--color-text-light);
  font-size: 0.875rem;
  text-decoration: none;
  cursor: not-allowed;
}

.icon-inline {
  display: inline-block;
  vertical-align: middle;
  margin-right: 0.25rem;
}

@media (max-width: 640px) {
  .report-meta-grid {
    grid-template-columns: 1fr;
  }
  .modal-footer {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  .modal-footer .footer-left,
  .modal-footer .footer-right {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .modal-footer .btn,
  .modal-footer button {
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }
}

/* タイムラインUI */
.timeline-container {
  position: relative;
  padding-left: 2rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.timeline-container::before {
  content: '';
  position: absolute;
  left: 0.75rem;
  top: 0.5rem;
  bottom: 0.5rem;
  width: 2px;
  background-color: var(--color-border);
}

.timeline-item {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.timeline-badge {
  position: absolute;
  left: -2rem;
  width: 1.6rem;
  height: 1.6rem;
  background: white;
  border: 2px solid var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.timeline-badge-icon {
  font-size: 0.8rem;
}

.timeline-card {
  flex: 1;
  background: var(--color-background);
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.timeline-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.timeline-text {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--text-main);
  font-weight: 500;
}

/* おすすめ観光地カード */
.spots-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.25rem;
  margin-top: 0.75rem;
}

@media (min-width: 640px) {
  .spots-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.spot-card {
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.spot-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
}

.spot-card-image-box {
  height: 140px;
  width: 100%;
}

.spot-card-image-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spot-card-info {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.spot-card-title {
  margin: 0 0 0.5rem 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-main);
}

.spot-card-desc {
  margin: 0 0 1rem 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.5;
  flex: 1;
}

.spot-card-link-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--primary-color);
  text-decoration: none;
  background: rgba(180, 207, 158, 0.06);
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.spot-card-link-btn:hover {
  background: rgba(180, 207, 158, 0.14);
}

.report-spots-fallback-text {
  font-size: 0.875rem;
  color: var(--text-main);
  line-height: 1.6;
  margin: 0.25rem 0 0 0;
}
</style>

